import { useIntl } from 'react-intl';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import { removeTrack, updateTrack } from '@alltrails/tracks/api/tracksRequests';
import { ButtonConfig } from '../../hooks/useAdminCTAs';
import { useAppDispatch, useAppSelector } from '../../redux';
import { optimisticallyHideMap, updateHoveredAdminResult } from '../../redux/reducer';
import { AdminMapHit, AdminTrackHit } from './utils';

/**
 * This component currently only implements a subset of the functionality from the monolith.
 * See the method below for implementation of missing links:
 * https://github.com/alltrails/alltrails/blob/a00887a87114a5dd6a3fe0b2b6cb35db81eef537/app/javascript/application/javascripts/react_components/mixins/map/map_mixin.js#L647
 */

const useAdminTrackLinks = (track?: AdminTrackHit | AdminMapHit) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const trailId = useAppSelector(state => state.map.trailId);
  const { hasPermission } = useAuthorization();
  const isAdmin = hasPermission({ permission: 'trails:manage' });
  const isPLP = hasPermission({ permission: 'public_lands:manage' });

  if (!isAdmin && !isPLP) {
    return;
  }

  if (!track) {
    return;
  }

  // After hide/remove: drop the recording from the overlay and close the hovercard.
  const onMutated = () => {
    dispatch(optimisticallyHideMap(track.ID));
    dispatch(updateHoveredAdminResult(undefined));
  };

  const linksConfig: ButtonConfig[] = [];
  if (isAdmin) {
    // "Hide" reuses the pre-existing localized strings (leaving them untranslated would strip them from
    // every app's locale files). The new "Remove" strings are admin-only, so they're plain literals.
    linksConfig.push({
      label: intl.formatMessage({ defaultMessage: 'Hide' }),
      id: 'hide',
      labelInProgress: intl.formatMessage({ defaultMessage: 'Hiding...' }),
      labelSuccess: intl.formatMessage({ defaultMessage: 'Hidden' }),
      labelFailed: intl.formatMessage({ defaultMessage: 'Failed' }),
      // trailId keeps the recording linked to the trail while it's hidden.
      handleClick: async () => {
        await updateTrack(track.ID, { hidden: true }, trailId);
        onMutated();
      }
    });
    linksConfig.push({
      label: 'Remove',
      id: 'remove',
      labelInProgress: 'Removing...',
      labelSuccess: 'Removed',
      labelFailed: 'Failed',
      handleClick: async () => {
        await removeTrack(track.ID);
        onMutated();
      }
    });
  }

  // This check means this CTA is available to both Admins and PLP users
  // while currently duplicative this check is left in to indicate that such a check should be added for each CTA added to this hook since some will be admin-only
  if (!track.private && (isAdmin || isPLP)) {
    linksConfig.push({
      label: intl.formatMessage({ defaultMessage: 'Add as new trail' }),
      id: 'add-as-new-trail',
      handleClick() {
        window.open(`/trail/new?recording=${track.ID}`, '_blank');
      }
    });
  }
  return linksConfig;
};

export default useAdminTrackLinks;
